import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Minus, Plus } from '@gravity-ui/icons';
import { getControlTimeline } from '../../../services/controlTimeline';
import { formatAxisTick } from '../../../utils/formatInTimezone';
import CameraNumber from '../../../components/CameraNumber/CameraNumber';
import { withCameraWord } from '../../../utils/hierarchyLabels';
import { buildControlRequest } from './useControlSummary';
import {
  TIMELINE_LEGEND,
  describeTimelineSegment,
  layoutTimelineRow,
  summarizeTimelineRow,
} from './controlTimelineModel';

const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
const MISSING_STATUSES = new Set([404, 501]);
// Масштаб — во сколько раз шкала шире видимой области; как в диаграмме кадров.
const ZOOM_LEVELS = [1, 2, 4, 8];
const TICKS_PER_SCREEN = 4;

/*
 * ТЗ V5 №11: цветная шкала состояния камер над «Камеры и простои».
 *
 * Отдельный читающий GET /analytics/control-timeline с теми же period + scope
 * + control_*, что и таблица: те же камеры, тот же порядок и страница, тот же
 * применённый интервал. Новых расчётов 1/30 дней не запускает.
 *
 * Маршрут ещё может отсутствовать (404/501) — тогда блок не показывается, а
 * таблица работает как прежде.
 */
const ControlTimeline = ({ range, scope, tableState, enabled }) => {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [reloadToken, setReloadToken] = useState(0);
  const [zoomIndex, setZoomIndex] = useState(0);
  const zoom = ZOOM_LEVELS[zoomIndex];
  const seqRef = useRef(0);

  const requestKey = useMemo(
    () => JSON.stringify(buildControlRequest({ range, scope, tableState })),
    [range, scope, tableState]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const params = JSON.parse(requestKey);
    if (!params.from || !params.to) return undefined;
    const seq = ++seqRef.current;
    const controller = new AbortController();
    setStatus('loading');
    getControlTimeline(params, { signal: controller.signal })
      .then((response) => {
        if (seq !== seqRef.current) return;
        setData(response);
        setStatus('ready');
      })
      .catch((error) => {
        if (seq !== seqRef.current || isCanceled(error)) return;
        setStatus(MISSING_STATUSES.has(error?.response?.status) ? 'missing' : 'error');
      });
    return () => controller.abort();
  }, [enabled, requestKey, reloadToken]);

  const fromAt = data?.from_at || range?.from;
  const toAt = data?.to_at || range?.to;
  const rows = useMemo(() => (data?.items || []).map((row) => {
    const layout = layoutTimelineRow(row.segments, fromAt, toAt);
    return { row, layout, summary: summarizeTimelineRow(layout) };
  }), [data, fromAt, toAt]);

  const axisTimezone = data?.items?.[0]?.display_timezone;
  const ticks = useMemo(() => {
    const start = new Date(fromAt).getTime();
    const end = new Date(toAt).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
    const withDate = end - start > 86400000;
    // Подписей столько, чтобы на каждом экране шкалы их было около четырёх.
    const intervals = TICKS_PER_SCREEN * zoom;
    return Array.from({ length: intervals + 1 }, (_, index) => {
      const ratio = index / intervals;
      const iso = new Date(start + (end - start) * ratio).toISOString();
      return { key: index, left: ratio * 100, label: formatAxisTick(iso, axisTimezone, withDate) };
    });
  }, [fromAt, toAt, axisTimezone, zoom]);

  if (status === 'missing') return null;

  const renderBody = () => {
    if (status === 'error') {
      return (
        <div className="pp-state pp-state--error" role="alert">
          <p>Не удалось загрузить диаграмму</p>
          <button type="button" className="pp-btn pp-btn--primary" onClick={() => setReloadToken((t) => t + 1)}>
            Повторить
          </button>
        </div>
      );
    }
    if (!data) return <div role="status" aria-live="polite">Загрузка…</div>;
    if (rows.length === 0) return <p className="pp-timeline-empty">Камер по этим фильтрам нет</p>;

    return (
      <>
        <div className="pp-timeline-head">
          <ul className="pp-timeline-legend" aria-label="Обозначения">
            {TIMELINE_LEGEND.map((item) => (
              <li key={item.key}>
                <span className={`pp-timeline-swatch pp-timeline-seg--${item.tone}`} aria-hidden="true" />
                {item.label}
              </li>
            ))}
          </ul>
          <span className="pp-timeline-zoom">
            <button type="button" className="pp-timeline-zoom-btn" onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
              disabled={zoomIndex === 0} title="Мельче" aria-label="Мельче">
              <Minus width={16} height={16} />
            </button>
            <button type="button" className="pp-timeline-zoom-btn"
              onClick={() => setZoomIndex((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1} title="Крупнее" aria-label="Крупнее">
              <Plus width={16} height={16} />
            </button>
          </span>
        </div>
        {/* Шкала шире экрана в zoom раз и прокручивается вбок; столбец с
            именами камер прилипает к левому краю. */}
        <div className="pp-timeline-scroll">
        <div className="pp-timeline-grid" style={{ width: `${zoom * 100}%` }} data-zoom={zoom}>
          {rows.map(({ row, layout, summary }) => (
            <div key={row.camera_id} className="pp-timeline-row">
              <div className="pp-timeline-name" title={row.camera_name}>
                <span>
                  {row.camera_name}
                  <CameraNumber id={row.camera_id} />
                </span>
                <small>{[row.object_name, row.location_name].filter(Boolean).join(' / ')}</small>
              </div>
              <div
                className="pp-timeline-bar"
                role="img"
                aria-label={`${withCameraWord(row.camera_name)}. ${summary}`}
              >
                {layout.map((segment) => (
                  <span
                    key={`${segment.fromMs}-${segment.status}`}
                    className={`pp-timeline-seg pp-timeline-seg--${segment.tone}${segment.inferred ? ' pp-timeline-seg--inferred' : ''}`}
                    style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                    title={describeTimelineSegment(segment, row.display_timezone)}
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="pp-timeline-row pp-timeline-row--axis" aria-hidden="true">
            <div />
            <div className="pp-timeline-axis">
              {ticks.map((tick) => (
                <span key={tick.key} style={{ left: `${tick.left}%` }}>{tick.label}</span>
              ))}
            </div>
          </div>
        </div>
        </div>
      </>
    );
  };

  return (
    <section className={`pp-card pp-timeline${status === 'loading' && data ? ' pp-control--stale' : ''}`}>
      <button
        type="button"
        className="pp-timeline-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="pp-card-title">Хронология работы камер</span>
        {open ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
      </button>
      {open && renderBody()}
    </section>
  );
};

export default ControlTimeline;
